import React, { useState, useEffect, useRef } from 'react';
import Webcam from 'react-webcam';
import { API_BASE_URL } from '../config';
import './EventoEntregaSeguridad.css';

export default function EventoEntregaSeguridad({ usuario, personaId, personaNombre, contratistaId, empresaId, onClose }) {
    const [step, setStep] = useState('articulos'); // 'articulos', 'captura', 'resumen'
    const [articulos, setArticulos] = useState([]);
    const [articulosDisponibles, setArticulosDisponibles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Estados para foto
    const [fotoData, setFotoData] = useState(null);
    const [modoCaptura, setModoCaptura] = useState(''); // 'camara', 'archivo'
    const webcamRef = useRef(null);
    const [validandoFoto, setValidandoFoto] = useState(false);
    const [fotoValidada, setFotoValidada] = useState(false);
    const [mensajeValidacion, setMensajeValidacion] = useState('');
    const [resultadoValidacion, setResultadoValidacion] = useState(null);

    // Estados para firma
    const [firmaData, setFirmaData] = useState(null);
    const canvasRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);

    // Estados para observaciones
    const [observaciones, setObservaciones] = useState('');
    const [fechaEntrega, setFechaEntrega] = useState(new Date().toISOString().split('T')[0]);

    useEffect(() => {
        fetchArticulos();
    }, []);

    const fetchWithAuth = async (url, options = {}) => {
        const token = localStorage.getItem('authToken');
        return fetch(url, {
            ...options,
            headers: {
                ...options.headers,
                'Authorization': `Bearer ${token}`
            }
        });
    };

    const fetchArticulos = async () => {
        try {
            setLoading(true);
            const response = await fetchWithAuth(`${API_BASE_URL}/api/articulos-seguridad?id_empresa=${empresaId}`);
            
            if (response.ok) {
                const data = await response.json();
                setArticulosDisponibles(data);
            } else {
                setError('Error al cargar artículos');
            }
        } catch (err) {
            console.error('Error al cargar artículos:', err);
            setError('Error al cargar artículos');
        } finally {
            setLoading(false);
        }
    };

    const handleToggleArticulo = (articulo) => {
        const existe = articulos.find(a => a.id_articulo === articulo.id);

        if (existe) {
            setArticulos(articulos.filter(a => a.id_articulo !== articulo.id));
            return;
        }

        setArticulos([
            ...articulos,
            {
                id_articulo: articulo.id,
                nombre: articulo.nombre,
                categoria: articulo.categoria,
                requiere_talla: articulo.requiere_talla,
                tallas_disponibles: articulo.tallas || [],
                talla_entregada: '',
                cantidad: 1,
                observaciones: ''
            }
        ]);
    };

    const handleSeleccionarTodos = () => {
        setArticulos(articulosDisponibles.map(articulo => ({
            id_articulo: articulo.id,
            nombre: articulo.nombre,
            categoria: articulo.categoria,
            requiere_talla: articulo.requiere_talla,
            tallas_disponibles: articulo.tallas || [],
            talla_entregada: '',
            cantidad: 1,
            observaciones: ''
        })));
    };

    const handleLimpiarSeleccion = () => {
        setArticulos([]);
    };

    const handleRemoverArticulo = (idArticulo) => {
        setArticulos(articulos.filter(a => a.id_articulo !== idArticulo));
    };

    const validarFotoTrabajador = async (fotoBase64) => {
        try {
            console.log('Iniciando validación de foto');
            setValidandoFoto(true);
            setMensajeValidacion('');
            setFotoValidada(false);
            setResultadoValidacion(null);

            console.log('Enviando solicitud a:', `${API_BASE_URL}/api/eventos-entrega-seguridad/validar-foto-trabajador`);
            const response = await fetchWithAuth(`${API_BASE_URL}/api/eventos-entrega-seguridad/validar-foto-trabajador`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id_personal: personaId,
                    foto_base64: fotoBase64,
                    id_contratista: contratistaId
                })
            });

            console.log('Respuesta recibida:', response.status);
            const data = await response.json();
            console.log('Datos:', data);

            if (!response.ok) {
                console.log('Validación fallida');
                setResultadoValidacion({
                    verified: false,
                    message: data.message || 'Error en validación facial',
                    similarity: data.similarity || 0,
                    expected_person: data.expected_person,
                    recognized_person: data.recognized_person
                });
                setMensajeValidacion(`❌ ${data.message || 'Error en validación facial'}`);
                setFotoValidada(false);
                return false;
            }

            console.log('Validación exitosa');
            setResultadoValidacion({
                verified: true,
                message: data.message || 'Validación exitosa',
                similarity: data.similarity,
                expected_person: data.expected_person,
                recognized_person: data.recognized_person
            });
            setMensajeValidacion(`✅ Foto validada exitosamente (${data.similarity.toFixed(1)}% similitud)`);
            setFotoValidada(true);
            return true;
        } catch (err) {
            console.error('Error al validar foto:', err);
            setMensajeValidacion('⚠️ Error al validar foto. Continuando sin validación facial...');
            setFotoValidada(true);
            return true;
        } finally {
            setValidandoFoto(false);
        }
    };

    const handleCapturarFoto = async () => {
        if (webcamRef.current) {
            const imageSrc = webcamRef.current.getScreenshot();
            await validarFotoTrabajador(imageSrc);
            setFotoData(imageSrc);
            setModoCaptura('');
        }
    };

    const handleSubirFoto = async (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = async (event) => {
                const fotoBase64 = event.target.result;
                setFotoData(fotoBase64);
                setModoCaptura('');
                
                await validarFotoTrabajador(fotoBase64);
            };
            reader.readAsDataURL(file);
        }
    };

    const getCanvasPoint = (event) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const touch = event.touches?.[0] || event.changedTouches?.[0];

        // Calcular la posición considerando el escalado del canvas
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;

        let clientX, clientY;
        if (touch) {
            clientX = touch.clientX;
            clientY = touch.clientY;
        } else {
            clientX = event.clientX;
            clientY = event.clientY;
        }

        return {
            x: (clientX - rect.left) * scaleX,
            y: (clientY - rect.top) * scaleY
        };
    };

    const handleCanvasMouseDown = (e) => {
        e.preventDefault();
        setIsDrawing(true);
        const canvas = canvasRef.current;
        const { x, y } = getCanvasPoint(e);
        const ctx = canvas.getContext('2d');
        ctx.beginPath();
        ctx.moveTo(x, y);
    };

    const handleCanvasMouseMove = (e) => {
        if (!isDrawing) return;
        e.preventDefault();
        const canvas = canvasRef.current;
        const { x, y } = getCanvasPoint(e);
        const ctx = canvas.getContext('2d');
        ctx.lineTo(x, y);
        ctx.stroke();
    };

    const handleCanvasMouseUp = (e) => {
        if (e) {
            e.preventDefault();
        }
        const canvas = canvasRef.current;
        if (canvas && isDrawing) {
            const ctx = canvas.getContext('2d');
            ctx.closePath();
            setFirmaData(canvas.toDataURL());
        }
        setIsDrawing(false);
    };

    const handleLimpiarFirma = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        setFirmaData(null);
    };

    const handleGuardarFirma = () => {
        const canvas = canvasRef.current;
        setFirmaData(canvas.toDataURL());
    };

    const handleCompletarEntrega = async () => {
        if (articulos.length === 0) {
            setError('Debe agregar al menos un artículo');
            return;
        }

        // Validar que los artículos que requieren talla tengan una seleccionada
        const sinTalla = articulos.find(a => a.requiere_talla && !a.talla_entregada);
        if (sinTalla) {
            setError(`Debe seleccionar una talla para el artículo "${sinTalla.nombre}"`);
            return;
        }

        const canvas = canvasRef.current;
        const firmaFinal = firmaData || (canvas ? canvas.toDataURL() : null);

        try {
            setSaving(true);
            setError('');

            // Crear evento de entrega
            const eventoResponse = await fetchWithAuth(`${API_BASE_URL}/api/eventos-entrega-seguridad`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id_personal: personaId,
                    id_contratista: contratistaId,
                    id_empresa: empresaId,
                    entregado_por: usuario.id,
                    fecha_entrega: fechaEntrega,
                    observaciones
                })
            });

            if (!eventoResponse.ok) {
                const errorData = await eventoResponse.json();
                throw new Error(errorData.error || 'Error al crear evento de entrega');
            }

            const evento = await eventoResponse.json();

            // Agregar artículos al evento
            for (const articulo of articulos) {
                const detalleResponse = await fetchWithAuth(
                    `${API_BASE_URL}/api/eventos-entrega-seguridad/${evento.id}/detalles`,
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            id_articulo: articulo.id_articulo,
                            talla_entregada: articulo.talla_entregada || null,
                            cantidad: articulo.cantidad,
                            observaciones: articulo.observaciones
                        })
                    }
                );

                if (!detalleResponse.ok) {
                    throw new Error('Error al agregar artículo a la entrega');
                }
            }

            // Completar evento con foto y firma
            const completarResponse = await fetchWithAuth(
                `${API_BASE_URL}/api/eventos-entrega-seguridad/${evento.id}/completar`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        foto_entrega: fotoData,
                        firma_entrega: firmaFinal
                    })
                }
            );

            if (!completarResponse.ok) {
                throw new Error('Error al completar evento de entrega');
            }

            const completarData = await completarResponse.json();
            if (completarData.correo_enviado) {
                setSuccess('Entrega registrada exitosamente y correo enviado al trabajador');
            } else {
                setSuccess('Entrega registrada exitosamente, pero no se pudo enviar el correo de confirmación');
            }
            setTimeout(() => {
                if (onClose) onClose();
            }, 1500);
        } catch (err) {
            console.error('Error al completar entrega:', err);
            setError(err.message || 'Error al completar entrega');
        } finally {
            setSaving(false);
        }
    };
    return (
        <>
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content evento-entrega-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>📦 Entrega de Artículos</h2>
                    <button className="close-btn" onClick={onClose}>×</button>
                </div>

                <div className="modal-body">
                    {error && <div className="error-message">{error}</div>}
                    {success && <div className="success-message">{success}</div>}

                    {/* PASO 1: SELECCIONAR ARTÍCULOS */}
                    {step === 'articulos' && (
                        <div className="evento-step">
                            <h3>Paso 1: Seleccionar Artículos</h3>
                            <p className="step-subtitle">Trabajador: <strong>{personaNombre}</strong></p>

                            <div className="articulos-disponibles">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', gap: '12px', paddingTop: '12px', paddingBottom: '8px' }}>
                                    <h4 style={{ margin: 0 }}>Artículos Disponibles</h4>
                                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                        <button className="btn-secondary" onClick={handleSeleccionarTodos} type="button">
                                            Seleccionar todos
                                        </button>
                                        <button className="btn-secondary" onClick={handleLimpiarSeleccion} type="button">
                                            Limpiar selección
                                        </button>
                                    </div>
                                </div>
                                <div className="articulos-grid">
                                    {articulosDisponibles.map(articulo => (
                                        <label key={articulo.id} className={`articulo-card ${articulos.some(a => a.id_articulo === articulo.id) ? 'selected' : ''}`}>
                                            <div className="articulo-info">
                                                <input
                                                    type="checkbox"
                                                    checked={articulos.some(a => a.id_articulo === articulo.id)}
                                                    onChange={() => handleToggleArticulo(articulo)}
                                                />
                                                <div>
                                                    <div className="articulo-nombre">{articulo.nombre}</div>
                                                    <div className="articulo-categoria">{articulo.categoria}</div>
                                                    {articulo.requiere_talla && <div className="articulo-talla-req">Requiere talla</div>}
                                                </div>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {articulos.length > 0 && (
                                <div className="articulos-seleccionados">
                                    <h4>Artículos Seleccionados ({articulos.length})</h4>
                                    <div className="articulos-lista">
                                        {articulos.map((articulo, idx) => (
                                            <div key={idx} className="articulo-seleccionado">
                                                <div className="articulo-detalles">
                                                    <div className="articulo-nombre">{articulo.nombre}</div>
                                                    <div className="articulo-cantidad">Cantidad: {articulo.cantidad}</div>
                                                    {articulo.requiere_talla && (
                                                        articulo.tallas_disponibles && articulo.tallas_disponibles.length > 0 ? (
                                                            <select
                                                                value={articulo.talla_entregada}
                                                                onChange={(e) => {
                                                                    const nuevosArticulos = [...articulos];
                                                                    nuevosArticulos[idx].talla_entregada = e.target.value;
                                                                    setArticulos(nuevosArticulos);
                                                                }}
                                                                className="input-talla"
                                                            >
                                                                <option value="">Seleccione talla...</option>
                                                                {articulo.tallas_disponibles.map(t => (
                                                                    <option key={t.id} value={t.talla}>{t.talla}</option>
                                                                ))}
                                                            </select>
                                                        ) : (
                                                            <div className="input-talla" style={{ color: '#dc2626', fontSize: '0.85rem' }}>
                                                                No hay tallas definidas para este artículo
                                                            </div>
                                                        )
                                                    )}
                                                    <input
                                                        type="text"
                                                        placeholder="Observaciones"
                                                        value={articulo.observaciones}
                                                        onChange={(e) => {
                                                            const nuevosArticulos = [...articulos];
                                                            nuevosArticulos[idx].observaciones = e.target.value;
                                                            setArticulos(nuevosArticulos);
                                                        }}
                                                        className="input-observaciones"
                                                    />
                                                </div>
                                                <button
                                                    className="btn-remover"
                                                    onClick={() => handleRemoverArticulo(articulo.id_articulo)}
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="evento-fecha-observaciones">
                                <div className="fecha-field">
                                    <label>Fecha de Entrega</label>
                                    <input
                                        type="date"
                                        value={fechaEntrega}
                                        onChange={(e) => setFechaEntrega(e.target.value)}
                                    />
                                </div>
                                <div className="observaciones-field">
                                    <label>Observaciones Generales</label>
                                    <textarea
                                        value={observaciones}
                                        onChange={(e) => setObservaciones(e.target.value)}
                                        placeholder="Observaciones sobre la entrega..."
                                        rows="3"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* PASO 2: CAPTURA DE FOTO Y FIRMA */}
                    {step === 'captura' && (
                        <div className="evento-step">
                            <h3>Paso 2: Capturar Foto y Firma</h3>

                            <div className="captura-container">
                                {/* FOTO */}
                                <div className="captura-seccion">
                                    <h4>📷 Foto de la Entrega</h4>
                                    {!fotoData && !modoCaptura && (
                                        <div className="captura-opciones">
                                            <button
                                                className="btn-captura"
                                                onClick={() => setModoCaptura('camara')}
                                            >
                                                📷 Capturar desde Cámara
                                            </button>
                                            <button
                                                className="btn-captura"
                                                onClick={() => document.getElementById('file-input-foto').click()}
                                            >
                                                📁 Subir Archivo
                                            </button>
                                            <input
                                                id="file-input-foto"
                                                type="file"
                                                accept="image/*"
                                                onChange={handleSubirFoto}
                                                style={{ display: 'none' }}
                                            />
                                        </div>
                                    )}

                                    {modoCaptura === 'camara' && (
                                        <div className="webcam-container">
                                            <Webcam
                                                ref={webcamRef}
                                                screenshotFormat="image/jpeg"
                                                width="100%"
                                            />
                                            <div className="webcam-botones">
                                                <button
                                                    className="btn-primary"
                                                    onClick={async () => {
                                                        console.log('CLICK BOTÓN CAPTURAR');
                                                        if (webcamRef.current) {
                                                            console.log('Webcam disponible');
                                                            const imageSrc = webcamRef.current.getScreenshot();
                                                            console.log('Foto capturada');
                                                            await validarFotoTrabajador(imageSrc);
                                                            setFotoData(imageSrc);
                                                            setModoCaptura('');
                                                        } else {
                                                            console.log('Webcam NO disponible');
                                                        }
                                                    }}
                                                    disabled={validandoFoto}
                                                    type="button"
                                                >
                                                    {validandoFoto ? 'Validando...' : 'Capturar Foto'}
                                                </button>
                                                <button
                                                    className="btn-secondary"
                                                    onClick={() => setModoCaptura('')}
                                                    disabled={validandoFoto}
                                                    type="button"
                                                >
                                                    Cancelar
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {fotoData && (
                                        <div className="foto-preview">
                                            <img src={fotoData} alt="Foto de entrega" />
                                            
                                            {validandoFoto && (
                                                <div style={{
                                                    marginTop: '16px',
                                                    padding: '16px',
                                                    borderRadius: '8px',
                                                    backgroundColor: '#fef3c7',
                                                    border: '2px solid #f59e0b',
                                                    color: '#92400e',
                                                    fontSize: '14px',
                                                    textAlign: 'center'
                                                }}>
                                                    ⏳ Validando rostro...
                                                </div>
                                            )}
                                            
                                            {resultadoValidacion && !validandoFoto && (
                                                <div style={{
                                                    marginTop: '16px',
                                                    padding: '16px',
                                                    borderRadius: '8px',
                                                    backgroundColor: resultadoValidacion.verified ? '#f0fdf4' : '#fef2f2',
                                                    border: `2px solid ${resultadoValidacion.verified ? '#22c55e' : '#ef4444'}`,
                                                    color: resultadoValidacion.verified ? '#166534' : '#991b1b'
                                                }}>
                                                    <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '12px' }}>
                                                        {resultadoValidacion.verified ? '✅ VALIDACIÓN EXITOSA' : '❌ VALIDACIÓN FALLIDA'}
                                                    </div>
                                                    
                                                    <div style={{ marginBottom: '12px', fontSize: '14px' }}>
                                                        <strong>Mensaje:</strong> {resultadoValidacion.message}
                                                    </div>

                                                    <div style={{ marginBottom: '12px', fontSize: '14px' }}>
                                                        <strong>Similitud:</strong> {resultadoValidacion.similarity.toFixed(1)}%
                                                    </div>

                                                    {resultadoValidacion.expected_person && (
                                                        <div style={{ 
                                                            marginBottom: '12px', 
                                                            padding: '10px', 
                                                            backgroundColor: 'rgba(255,255,255,0.5)',
                                                            borderRadius: '4px',
                                                            fontSize: '14px'
                                                        }}>
                                                            <strong>Persona Esperada:</strong>
                                                            <div>{resultadoValidacion.expected_person.full_name}</div>
                                                            <div style={{ fontSize: '12px', opacity: 0.8 }}>RUT: {resultadoValidacion.expected_person.dni}</div>
                                                        </div>
                                                    )}

                                                    {resultadoValidacion.recognized_person && !resultadoValidacion.verified && (
                                                        <div style={{ 
                                                            marginBottom: '12px', 
                                                            padding: '10px', 
                                                            backgroundColor: 'rgba(255,255,255,0.5)',
                                                            borderRadius: '4px',
                                                            fontSize: '14px'
                                                        }}>
                                                            <strong>Persona Reconocida en Foto:</strong>
                                                            <div>{resultadoValidacion.recognized_person.full_name}</div>
                                                            <div style={{ fontSize: '12px', opacity: 0.8 }}>RUT: {resultadoValidacion.recognized_person.dni}</div>
                                                        </div>
                                                    )}

                                                    {resultadoValidacion.verified && resultadoValidacion.recognized_person && (
                                                        <div style={{ 
                                                            marginBottom: '12px', 
                                                            padding: '10px', 
                                                            backgroundColor: 'rgba(255,255,255,0.5)',
                                                            borderRadius: '4px',
                                                            fontSize: '14px'
                                                        }}>
                                                            <strong>Persona Reconocida:</strong>
                                                            <div>{resultadoValidacion.recognized_person.full_name}</div>
                                                            <div style={{ fontSize: '12px', opacity: 0.8 }}>RUT: {resultadoValidacion.recognized_person.dni}</div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            
                                            <button
                                                className="btn-secondary"
                                                onClick={() => {
                                                    setFotoData(null);
                                                    setModoCaptura('');
                                                    setFotoValidada(false);
                                                    setMensajeValidacion('');
                                                    setResultadoValidacion(null);
                                                }}
                                                style={{ marginTop: '12px' }}
                                            >
                                                Cambiar Foto
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* FIRMA */}
                                <div className="captura-seccion">
                                    <h4>✍️ Firma del Receptor</h4>
                                    <canvas
                                        ref={canvasRef}
                                        width={400}
                                        height={200}
                                        className="firma-canvas"
                                        onMouseDown={handleCanvasMouseDown}
                                        onMouseMove={handleCanvasMouseMove}
                                        onMouseUp={handleCanvasMouseUp}
                                        onMouseLeave={handleCanvasMouseUp}
                                        onTouchStart={handleCanvasMouseDown}
                                        onTouchMove={handleCanvasMouseMove}
                                        onTouchEnd={handleCanvasMouseUp}
                                        onTouchCancel={handleCanvasMouseUp}
                                    />
                                    <div className="firma-botones">
                                        <button
                                            className="btn-secondary"
                                            onClick={handleLimpiarFirma}
                                        >
                                            Limpiar
                                        </button>
                                        <button
                                            className="btn-primary"
                                            onClick={handleGuardarFirma}
                                            disabled={!firmaData}
                                        >
                                            Guardar Firma
                                        </button>
                                    </div>
                                    {firmaData && <div className="firma-guardada">✓ Firma capturada</div>}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* PASO 3: RESUMEN */}
                    {step === 'resumen' && (
                        <div className="evento-step">
                            <h3>Paso 3: Resumen de Entrega</h3>
                            <div className="resumen-contenido">
                                <div className="resumen-seccion">
                                    <h4>Información General</h4>
                                    <p><strong>Trabajador:</strong> {personaNombre}</p>
                                    <p><strong>Fecha:</strong> {new Date(fechaEntrega).toLocaleDateString('es-CL')}</p>
                                    {observaciones && <p><strong>Observaciones:</strong> {observaciones}</p>}
                                </div>

                                <div className="resumen-seccion">
                                    <h4>Artículos Entregados ({articulos.length})</h4>
                                    <ul className="resumen-articulos">
                                        {articulos.map((a, idx) => (
                                            <li key={idx}>
                                                {a.nombre} (Cantidad: {a.cantidad})
                                                {a.talla_entregada && <span> - Talla: {a.talla_entregada}</span>}
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <div className="resumen-seccion">
                                    <h4>Evidencia</h4>
                                    {fotoData && <div className="resumen-foto">✓ Foto capturada</div>}
                                    {firmaData && <div className="resumen-firma">✓ Firma capturada</div>}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="modal-footer">
                    <button
                        className="btn-secondary"
                        onClick={onClose}
                        disabled={saving}
                    >
                        Cancelar
                    </button>

                    {step === 'articulos' && (
                        <button
                            className="btn-primary"
                            onClick={() => setStep('captura')}
                            disabled={articulos.length === 0}
                        >
                            Siguiente: Captura →
                        </button>
                    )}

                    {step === 'captura' && (
                        <>
                            <button
                                className="btn-secondary"
                                onClick={() => setStep('articulos')}
                            >
                                ← Atrás
                            </button>
                            <button
                                className="btn-primary"
                                onClick={() => setStep('resumen')}
                            >
                                Siguiente: Resumen →
                            </button>
                        </>
                    )}

                    {step === 'resumen' && (
                        <>
                            <button
                                className="btn-secondary"
                                onClick={() => setStep('captura')}
                            >
                                ← Atrás
                            </button>
                            <button
                                className="btn-success"
                                onClick={handleCompletarEntrega}
                                disabled={saving}
                            >
                                {saving ? 'Guardando...' : '✓ Completar Entrega'}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
        </>
    );
}
